import { Component, OnInit, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { TabsModule } from 'primeng/tabs';
import { CheckboxModule } from 'primeng/checkbox';
import { ConfirmationService } from 'primeng/api';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { TableLazyLoadEvent } from 'primeng/table';
import { finalize } from 'rxjs';
import { LocalizationService } from '../../core/localization/localization.service';
import { AinFormFieldComponent } from '../../shared/design-system/form-field.component';
import { InfoHintComponent } from '../../shared/design-system/info-hint.component';
import { PartySelectorComponent } from '../../shared/design-system/party-selector.component';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { NotificationDispatchPolicy, QuotaPolicy, QuotaUsage, SuppressionEntry } from './notification.models';
import { NotificationService } from './notification.service';
import { maskDestinationHash, normalizeNotificationCategory, normalizeNotificationChannel, suppressionReasonLabelKey } from './notification.utils';
import { AinCanAccessDirective } from '../../shared/security/access.directive';
import { CurrentContextService } from '../../core/context/current-context.service';

@Component({
  standalone: true,
  selector: 'ain-notifications-policy',
  providers: [ConfirmationService],
  imports: [
    AinPageComponent, InfoHintComponent, AinFormFieldComponent, PartySelectorComponent,
    FormsModule, TableModule, ButtonModule, InputTextModule, InputNumberModule, SelectModule, TabsModule, CheckboxModule, ConfirmDialogModule, AinCanAccessDirective
  ],
  template: `
    <ain-page [eyebrow]="t('notifications.eyebrow')" [title]="t('notifications.policy.title')" [description]="t('notifications.policy.description')">
      <ain-info-hint page-title-addon [titleKey]="'notifications.policy.help.title'" [descriptionKey]="'notifications.policy.help.description'"
        [bulletKeys]="['notifications.policy.help.bullets.quotas','notifications.policy.help.bullets.suppressions','notifications.policy.help.bullets.quietHours']" />

      <p-tabs [value]="canViewQuotas() ? 'quotas' : 'dispatch'">
        <p-tablist>
          <p-tab ainCanAccess [ainCanAccessPermission]="'notifications.quotas.view'" value="quotas">{{ t('notifications.policy.tabs.quotas') }}</p-tab>
          <p-tab value="dispatch">{{ t('notifications.policy.tabs.dispatch') }}</p-tab>
          <p-tab value="suppressions">{{ t('notifications.policy.tabs.suppressions') }}</p-tab>
        </p-tablist>
        <p-tabpanels>
          <p-tabpanel ainCanAccess [ainCanAccessPermission]="'notifications.quotas.view'" value="quotas">
            <section class="card">
              <p-table [value]="quotaPolicies()" [paginator]="true" [rows]="10">
                <ng-template pTemplate="header"><tr><th>{{ t('notifications.messages.filters.channel') }}</th><th>{{ t('notifications.messages.filters.category') }}</th><th>{{ t('notifications.policy.columns.limit') }}</th><th>{{ t('notifications.policy.columns.window') }}</th><th>{{ t('notifications.policy.columns.usage') }}</th></tr></ng-template>
                <ng-template pTemplate="body" let-row>
                  <tr>
                    <td>{{ channelLabel(row.channel) }}</td>
                    <td>{{ categoryLabel(row.category) }}</td>
                    <td>{{ row.limitCount }}</td>
                    <td>{{ row.windowType }}</td>
                    <td>{{ usageFor(row.id) }}</td>
                  </tr>
                </ng-template>
              </p-table>
              <div ainCanAccess [ainCanAccessPermission]="'notifications.quotas.manage'" class="grid">
                <ain-form-field [label]="t('notifications.messages.filters.channel')"><p-select [options]="channelOptions" optionLabel="label" optionValue="value" [(ngModel)]="quotaForm.channel" appendTo="body" /></ain-form-field>
                <ain-form-field [label]="t('notifications.messages.filters.category')"><p-select [options]="categoryOptions" optionLabel="label" optionValue="value" [(ngModel)]="quotaForm.category" appendTo="body" /></ain-form-field>
                <ain-form-field [label]="t('notifications.policy.fields.windowType')"><p-select [options]="windowOptions" optionLabel="label" optionValue="value" [(ngModel)]="quotaForm.windowType" appendTo="body" /></ain-form-field>
                <ain-form-field [label]="t('notifications.policy.columns.limit')"><p-inputNumber [(ngModel)]="quotaForm.limitCount" [min]="1" /></ain-form-field>
              </div>
              <button ainCanAccess [ainCanAccessPermission]="'notifications.quotas.manage'" pButton type="button" [label]="t('common.save')" [loading]="saving()" (click)="saveQuota()"></button>
            </section>
          </p-tabpanel>
          <p-tabpanel value="dispatch">
            <section class="card">
              <p-table [value]="dispatchPolicies()" [paginator]="true" [rows]="10">
                <ng-template pTemplate="header"><tr><th>{{ t('notifications.messages.filters.channel') }}</th><th>{{ t('notifications.policy.fields.quietHours') }}</th><th>{{ t('notifications.policy.fields.frequencyCap') }}</th><th>{{ t('notifications.policy.fields.enabled') }}</th></tr></ng-template>
                <ng-template pTemplate="body" let-row>
                  <tr>
                    <td>{{ channelLabel(row.channel) }}</td>
                    <td>{{ row.quietHoursStart || '—' }} - {{ row.quietHoursEnd || '—' }}</td>
                    <td>{{ row.frequencyCapCount }} / {{ row.frequencyCapWindowMinutes }}m</td>
                    <td>{{ row.isEnabled ? t('common.yes') : t('common.no') }}</td>
                  </tr>
                </ng-template>
              </p-table>
              <div ainCanAccess [ainCanAccessPermission]="'notifications.policy.manage'" class="grid">
                <ain-form-field [label]="t('notifications.messages.filters.channel')"><p-select [options]="channelOptions" optionLabel="label" optionValue="value" [(ngModel)]="dispatchForm.channel" appendTo="body" /></ain-form-field>
                <ain-form-field [label]="t('notifications.messages.filters.category')"><p-select [options]="categoryOptions" optionLabel="label" optionValue="value" [(ngModel)]="dispatchForm.category" appendTo="body" /></ain-form-field>
                <ain-form-field [label]="t('notifications.policy.fields.timeZone')"><input pInputText [(ngModel)]="dispatchForm.timeZoneId" /></ain-form-field>
                <ain-form-field [label]="t('notifications.policy.fields.quietStart')"><input pInputText type="time" [(ngModel)]="dispatchForm.quietHoursStart" /></ain-form-field>
                <ain-form-field [label]="t('notifications.policy.fields.quietEnd')"><input pInputText type="time" [(ngModel)]="dispatchForm.quietHoursEnd" /></ain-form-field>
                <ain-form-field [label]="t('notifications.policy.fields.frequencyCap')"><p-inputNumber [(ngModel)]="dispatchForm.frequencyCapCount" [min]="0" /></ain-form-field>
                <ain-form-field [label]="t('notifications.policy.fields.frequencyWindow')"><p-inputNumber [(ngModel)]="dispatchForm.frequencyCapWindowMinutes" [min]="1" /></ain-form-field>
                <ain-form-field [label]="t('notifications.policy.fields.transactionalOverride')"><p-checkbox [(ngModel)]="dispatchForm.transactionalOverride" [binary]="true" /></ain-form-field>
              </div>
              <button ainCanAccess [ainCanAccessPermission]="'notifications.policy.manage'" pButton type="button" [label]="t('common.save')" [loading]="saving()" (click)="saveDispatch()"></button>
            </section>
          </p-tabpanel>
          <p-tabpanel value="suppressions">
            <section class="card">
              <div ainCanAccess [ainCanAccessPermission]="'notifications.policy.manage'" class="grid">
                <ain-form-field [label]="t('notifications.policy.fields.party')"><ain-party-selector [(ngModel)]="suppressionForm.partyId" /></ain-form-field>
                <ain-form-field [label]="t('notifications.policy.fields.reason')">
                  <p-select [options]="reasonOptions" optionLabel="label" optionValue="value" [(ngModel)]="suppressionForm.reason" appendTo="body" />
                </ain-form-field>
                <ain-form-field [label]="t('notifications.policy.fields.source')"><input pInputText [(ngModel)]="suppressionForm.source" /></ain-form-field>
              </div>
              <button ainCanAccess [ainCanAccessPermission]="'notifications.policy.manage'" pButton type="button" [label]="t('notifications.policy.actions.addSuppression')" [loading]="saving()" (click)="addSuppression()"></button>
              <p-table [value]="suppressions()" [lazy]="true" [paginator]="true" [rows]="suppressionPageSize" [totalRecords]="suppressionTotal()" (onLazyLoad)="loadSuppressions($event)" class="suppression-table">
                <ng-template pTemplate="header"><tr><th>{{ t('notifications.policy.fields.reason') }}</th><th>{{ t('notifications.policy.columns.state') }}</th><th>{{ t('notifications.policy.columns.contact') }}</th><th>{{ t('notifications.policy.fields.source') }}</th><th></th></tr></ng-template>
                <ng-template pTemplate="body" let-row>
                  <tr>
                    <td>{{ t(suppressionReasonKey(row.reason)) }}</td>
                    <td>{{ row.isActive ? t('notifications.policy.state.active') : t('notifications.policy.state.inactive') }}</td>
                    <td>{{ row.partyId ? t('notifications.policy.contact.party') : maskHash(row.contactHash || '') }}</td>
                    <td>{{ row.source }}</td>
                    <td>@if (row.isActive) { <button ainCanAccess [ainCanAccessPermission]="'notifications.policy.manage'" pButton size="small" severity="danger" [label]="t('notifications.policy.actions.revoke')" (click)="confirmRevoke(row)"></button> }</td>
                  </tr>
                </ng-template>
              </p-table>
            </section>
          </p-tabpanel>
        </p-tabpanels>
      </p-tabs>
      <p-confirmDialog />
    </ain-page>
  `,
  styles: [`.card{border:1px solid #e5e7eb;border-radius:12px;padding:12px;margin-bottom:12px}.grid{display:grid;gap:10px;grid-template-columns:repeat(auto-fit,minmax(180px,1fr));margin:10px 0}.suppression-table{margin-top:12px}`]
})
export class NotificationsPolicyComponent implements OnInit {
  private readonly api = inject(NotificationService);
  private readonly i18n = inject(LocalizationService);
  private readonly confirmation = inject(ConfirmationService);
  private readonly context = inject(CurrentContextService);

  readonly saving = signal(false);
  readonly quotaPolicies = signal<QuotaPolicy[]>([]);
  readonly quotaUsage = signal<QuotaUsage[]>([]);
  readonly dispatchPolicies = signal<NotificationDispatchPolicy[]>([]);
  readonly suppressions = signal<SuppressionEntry[]>([]);
  readonly suppressionTotal = signal(0);
  readonly suppressionPageSize = 12;

  quotaForm = { channel: 'Email', category: 'Marketing', windowType: 'Daily', limitCount: 1000, isEnabled: true, smsSegmentWeight: 1 };
  dispatchForm = { channel: 'Email', category: 'Marketing', timeZoneId: 'Asia/Tehran', quietHoursStart: '22:00', quietHoursEnd: '08:00', frequencyCapCount: 3, frequencyCapWindowMinutes: 1440, transactionalOverride: true, isEnabled: true };
  suppressionForm = { partyId: null as string | null, reason: 'Manual', source: 'admin-ui' };

  readonly channelOptions = [{ label: 'Email', value: 'Email' }, { label: 'SMS', value: 'Sms' }];
  readonly categoryOptions = [{ label: 'Transactional', value: 'Transactional' }, { label: 'Service', value: 'Service' }, { label: 'Marketing', value: 'Marketing' }];
  readonly windowOptions = [{ label: 'Daily', value: 'Daily' }, { label: 'Monthly', value: 'Monthly' }];
  readonly reasonOptions = [
    { label: 'Opt-out', value: 'OptOut' },
    { label: 'Hard bounce', value: 'HardBounce' },
    { label: 'Complaint', value: 'Complaint' },
    { label: 'Stop keyword', value: 'StopKeyword' },
    { label: 'Manual', value: 'Manual' }
  ];

  ngOnInit(): void { this.loadAll(); }
  t(key: string): string { return this.i18n.translate(key); }
  channelLabel(channel: unknown): string { return this.t(`notifications.enums.channel.${normalizeNotificationChannel(channel as never)}`); }
  categoryLabel(category: unknown): string { return this.t(`notifications.enums.category.${normalizeNotificationCategory(category as never) ?? 'Other'}`); }
  suppressionReasonKey = suppressionReasonLabelKey;
  maskHash = maskDestinationHash;
  canViewQuotas(): boolean { return this.canAccess('notifications.quotas.view'); }

  usageFor(policyId: string): string {
    const usage = this.quotaUsage().find(x => x.policyId === policyId);
    if (!usage) return '—';
    return `${usage.consumedCount}/${usage.consumedCount + usage.availableCount}`;
  }

  loadAll(): void {
    if (this.canAccess('notifications.quotas.view')) {
      this.api.listQuotaPolicies().subscribe(items => this.quotaPolicies.set(items));
      this.api.listQuotaUsage().subscribe(items => this.quotaUsage.set(items));
    }
    this.api.listDispatchPolicies().subscribe(items => this.dispatchPolicies.set(items));
    this.api.listSuppressions(true, 0, this.suppressionPageSize).subscribe(page => {
      this.suppressions.set(page.items);
      this.suppressionTotal.set(page.totalCount);
    });
  }

  saveQuota(): void {
    if (!this.canAccess('notifications.quotas.manage')) return;
    this.saving.set(true);
    this.api.upsertQuotaPolicy(this.quotaForm).pipe(finalize(() => this.saving.set(false))).subscribe(() => this.loadAll());
  }

  saveDispatch(): void {
    if (!this.canAccess('notifications.policy.manage')) return;
    this.saving.set(true);
    this.api.upsertDispatchPolicy({
      ...this.dispatchForm,
      quietHoursStart: this.dispatchForm.quietHoursStart ? `${this.dispatchForm.quietHoursStart}:00` : null,
      quietHoursEnd: this.dispatchForm.quietHoursEnd ? `${this.dispatchForm.quietHoursEnd}:00` : null
    }).pipe(finalize(() => this.saving.set(false))).subscribe(() => this.loadAll());
  }

  addSuppression(): void {
    if (!this.canAccess('notifications.policy.manage')) return;
    this.saving.set(true);
    this.api.createSuppression({
      channel: null,
      category: null,
      partyId: this.suppressionForm.partyId,
      contactHash: null,
      reason: this.suppressionForm.reason,
      source: this.suppressionForm.source,
      expiresAtUtc: null
    }).pipe(finalize(() => this.saving.set(false))).subscribe(() => this.loadAll());
  }

  loadSuppressions(event: TableLazyLoadEvent): void {
    this.api.listSuppressions(true, event.first ?? 0, event.rows ?? this.suppressionPageSize).subscribe(page => {
      this.suppressions.set(page.items);
      this.suppressionTotal.set(page.totalCount);
    });
  }

  confirmRevoke(entry: SuppressionEntry): void {
    if (!this.canAccess('notifications.policy.manage')) return;
    this.confirmation.confirm({
      header: this.t('notifications.policy.actions.revoke'),
      message: this.t('notifications.policy.confirm.revoke'),
      accept: () => this.api.revokeSuppression(entry.id, 'operator_revoked').subscribe(() => this.loadAll())
    });
  }

  private canAccess(permission: string): boolean {
    const context = this.context.context();
    return (context?.roles ?? []).includes('SystemAdmin') ||
      (context?.permissions ?? []).some(item => item.key === permission);
  }
}
